import { Column, Row } from 'react-table';
import { getSortByRule, Time } from '@nbirl/nbi-common-ui-libs';
import { orderBy as _orderBy } from 'lodash';

import { FavouriteToggle } from '../../favourites/components';
import { Folder } from '../../folders/models';
import {
  DocumentListNameCell,
  DocumentsListActionsCell,
  DocumentStatusPill,
} from '../components';
import {
  DocumentListRow,
  DocumentSearchResult,
  SecureDocument,
} from '../models';

export const orderBySortString = <T,>(data: T[], sortString: string) => {
  const sortByRule = getSortByRule(sortString);
  const id = sortByRule.id === 'addedBy' ? 'creator.name' : sortByRule.id;
  return _orderBy(data, id, sortByRule.desc ? 'desc' : 'asc');
};

export const getDocumentRowCells = (el: HTMLDivElement) =>
  el?.firstChild?.firstChild?.lastChild?.firstChild?.childNodes as
    | NodeListOf<HTMLTableCellElement>
    | undefined;

export const getDocumentRowProps = (row: Row<any>) => ({
  style: { height: '56px' },
});

type DocumentsListColumnsProps = {
  isSearching: boolean;
  updatingStatuses: string[];
  subFolders: Folder[];
  disableStatusSort?: boolean;
  onDocumentStatusUpdateStart: (id: string) => void;
  onDocumentStatusUpdateFinish: (id: string) => void;
  onFolderChange: (id: string) => void;
  onFolderTreeChange: () => void;
  onRefetchList: () => void;
  onShareDocument: (id: string) => void;
};

export const getDocumentListColumns = ({
  isSearching,
  updatingStatuses,
  subFolders,
  disableStatusSort,
  onDocumentStatusUpdateStart,
  onDocumentStatusUpdateFinish,
  onFolderChange,
  onFolderTreeChange,
  onRefetchList,
  onShareDocument,
}: DocumentsListColumnsProps): Column<DocumentListRow>[] => [
  {
    Header: () => <div className="ml-8">Name</div>,
    accessor: 'name',
    Cell: ({
      value,
      cell: {
        row: { original },
      },
    }) => (
      <DocumentListNameCell
        row={original}
        value={value}
        isSearching={isSearching}
      />
    ),
  },
  {
    Header: 'Added by',
    id: 'addedBy',
    disableSortBy: true,
    accessor: (row) => row.creator.name,
  },
  {
    Header: 'Date added',
    accessor: 'dateAdded',
    Cell: ({ value }) =>
      value ? <Time value={value} representation="date" /> : null,
  },
  {
    Header: 'Status',
    disableSortBy: disableStatusSort,
    accessor: 'status',
    Cell: ({
      value,
      cell: {
        row: { original },
      },
    }) => {
      return value ? (
        <DocumentStatusPill
          status={value}
          isUpdating={updatingStatuses.includes(original.id)}
        />
      ) : null;
    },
  },
  {
    id: 'Favorite',
    accessor: 'isFavourite',
    Cell: ({
      cell: {
        row: { original },
      },
    }) => {
      return (
        <FavouriteToggle
          isFolder={original.isFolder}
          isFavorite={original.isFavourite}
          id={original.id}
          onChange={onRefetchList}
        />
      );
    },
  },
  {
    id: 'actions',
    accessor: 'id',
    disableSortBy: true,
    Cell: ({
      cell: {
        row: { original },
      },
    }) => (
      <DocumentsListActionsCell
        row={original}
        currentFolders={subFolders}
        isSearching={isSearching}
        onDocumentStatusUpdateStart={onDocumentStatusUpdateStart}
        onDocumentStatusUpdateFinish={onDocumentStatusUpdateFinish}
        onFolderChange={onFolderChange}
        onFolderTreeChange={onFolderTreeChange}
        onRefetchList={onRefetchList}
        onShareDocument={() => onShareDocument(original.id)}
      />
    ),
  },
];

export const getDocumentListData = (
  isSearching: boolean,
  sortBy: string,
  parentFolderId: string,
  searchResults: DocumentSearchResult[] = [],
  folders: Folder[] = [],
  documents: SecureDocument[] = []
): DocumentListRow[] =>
  isSearching
    ? searchResults.map((document) => ({ isFolder: false, ...document }))
    : [
        ...orderBySortString(folders, sortBy).map((folder) => ({
          isFolder: true,
          parentFolderId: folder.id,
          ...folder,
        })),
        ...orderBySortString(documents, sortBy).map((document) => ({
          isFolder: false,
          parentFolderId,
          ...document,
        })),
      ];
